import React, {useContext, useEffect, useState} from 'react'
import _ from 'lodash'
import {useGesture} from '@use-gesture/react'
import {Button, Input, Popup} from 'semantic-ui-react'
import {RxStopwatch} from 'react-icons/rx'
import {secondsToTimeString, timeStringToSeconds, useConfig} from '@/features/videolab'
import {
  IEvent,
  ILabel,
  IUserLabelsMap,
  LabelingContext,
  findEventOverlap,
  writeToLabelTrack,
} from '@/features/labeling'
import {ConfirmButton} from '@/components/confirm-button'
import {JsonViewer} from '@/components/json-viewer'
import {ICatalogStats} from '@/components/catalog'
import {ILabelingWidgetConfig} from './labeling-widgets'
import './style.css'

export * from './labeling-widgets'

export interface ILabelingPanelProps {
  predictionStats?: ICatalogStats
  labelingWidgetConfig: ILabelingWidgetConfig<object>
  data?: {
    predictions?: IEvent[]
  }
  selectedEvent?: IEvent
  currentTime?: number
  readOnly?: boolean
  userLabels?: IUserLabelsMap
  hideJsonViewer?: boolean
  setSelectedEvent?: (e: IEvent) => void
  findLabelFromPrediction: (event: IEvent) => ILabel<object> | undefined
  selectRandomPrediction?: () => void
}

export function SetFromCurrent(props: {
  currentTime?: number
  tooltip?: string
  setter: React.Dispatch<React.SetStateAction<number | undefined>>
}) {
  const {currentTime, tooltip, setter} = props
  if (currentTime === undefined) return null
  return (
    <Popup
      size='tiny'
      content={tooltip || 'set from current time'}
      trigger={
        <button
          onClick={() => setter(currentTime)}
          onFocus={(e) => e.target.blur()}
          children={<RxStopwatch />}
        />
      }
    />
  )
}

export function LabelingPanel(props: ILabelingPanelProps) {
  const {
    predictionStats,
    labelingWidgetConfig,
    data,
    selectedEvent,
    currentTime,
    readOnly,
    userLabels,
    hideJsonViewer,
    selectRandomPrediction,
    setSelectedEvent,
    findLabelFromPrediction,
  } = {
    ...props,
  }

  const {formatTime} = useConfig()

  const {isLabelingAllowed, getCrdtLabelTrack} = useContext(LabelingContext)

  const [isEditing, setIsEditing] = useState(false)
  const [inputStartTime, setInputStartTime] = useState('')
  const [inputEndTime, setInputEndTime] = useState('')
  const [startTime, setStartTime] = useState<number | undefined>(undefined)
  const [endTime, setEndTime] = useState<number | undefined>(undefined)
  const [currentLabelData, setCurrentLabelData] = useState<object>()
  const [implicitPredictionData, setImplicitPredictionData] = useState<object>()
  const [implicitLabelData, setImplicitLabelData] = useState<object>()
  const [events, setEvents] = useState<{
    [type: string]: IEvent[]
  }>()

  useEffect(() => {
    const _events = _.chain(data)
      .get('predictions')
      .groupBy((v) => v.type)
      .value()
    setEvents(_events)
  }, [data])

  // timestamps
  useEffect(() => {
    setIsEditing(false)
    if (!selectedEvent) {
      setStartTime(undefined)
      setEndTime(undefined)
      return
    }
    if (!_.isEqual(selectedEvent.start, startTime)) setStartTime(selectedEvent.start)
    if (!_.isEqual(selectedEvent.end, endTime)) setEndTime(selectedEvent.end)
    if (!_.isEqual(selectedEvent.start, timeStringToSeconds(inputStartTime)))
      setInputStartTime(_.toString(selectedEvent.start))
    if (!_.isEqual(selectedEvent.end, timeStringToSeconds(inputEndTime)))
      setInputEndTime(_.toString(selectedEvent.end))
  }, [selectedEvent])

  useEffect(() => {
    setStartTime(timeStringToSeconds(inputStartTime))
  }, [inputStartTime])

  useEffect(() => {
    setEndTime(timeStringToSeconds(inputEndTime))
  }, [inputEndTime])

  useEffect(() => {
    if (
      !(
        selectedEvent &&
        selectedEvent.type &&
        events &&
        events[selectedEvent.type] &&
        events[selectedEvent.type].length > 0
      )
    ) {
      setImplicitPredictionData(undefined)
      return
    }

    const _events = events[selectedEvent.type]
    const _implicitPredictionData = _.chain(findEventOverlap(_events, selectedEvent))
      .reduce((prev, curr) => {
        return _.merge(_.cloneDeep(prev), curr)
      })
      .omit('type', 'start', 'end')
      .value()
    setImplicitPredictionData(_implicitPredictionData)
  }, [selectedEvent, events])

  // current and implied label data
  useEffect(() => {
    if (!selectedEvent) {
      setCurrentLabelData(undefined)
      setImplicitLabelData(undefined)
      return
    }
    const _selectedEventLabel: ILabel<object> | undefined =
      selectedEvent ? findLabelFromPrediction(selectedEvent) : undefined
    const _labelData: object | undefined =
      _selectedEventLabel ? _selectedEventLabel.data : undefined
    setCurrentLabelData(_labelData)

    const labels = handleGetLabelTrack(selectedEvent.type)
    if (!labels) {
      setImplicitLabelData(undefined)
      return
    }
    const _implicitLabelData = _.chain(
      findEventOverlap<ILabel<object>>(
        _.values(labels.toJSON()),
        _.pick(selectedEvent, 'type', 'start', 'end') as ILabel<object>
      )
    )
      .reduce((prev, curr) => {
        return _.merge(_.cloneDeep(prev), curr)
      })
      .get('data')
      .value()
    // console.log(_implicitLabelData);
    setImplicitLabelData(_implicitLabelData)
  }, [selectedEvent, userLabels])

  useGesture(
    {
      onKeyDown: ({event}) => {
        const t = event.target as HTMLInputElement
        if (isEditing && t.tagName === 'INPUT' && t.parentElement?.classList.contains('ts-edit')) {
          if (event.code === 'Enter') {
            event.stopPropagation()
            event.preventDefault()
            handleSaveTime()
          }
        }
        if (!_.includes(['BODY', 'A'], t.tagName)) return
        if (!selectedEvent) return
        const scorePath = 'score'
        let s: number | undefined = _.get(currentLabelData, scorePath)
        // console.log('onKeyDown', event.code, event.key, s);
        switch (event.key.toLowerCase()) {
          case 'i':
            if (currentTime === undefined) break
            if (selectedEvent.start === -1 && selectedEvent.end === -1) break
            // handleSaveTime(currentTime);
            handleSetSelectedEvent({
              ...selectedEvent,
              start: currentTime,
            })
            break
          case 'o':
            if (currentTime === undefined) break
            if (selectedEvent.start === -1 && selectedEvent.end === -1) break
            // handleSaveTime(undefined, currentTime);
            if (currentTime === selectedEvent.start) break
            handleSetSelectedEvent({
              ...selectedEvent,
              end: currentTime,
            })
            break
          case '-':
            if (selectedEvent.type === 'categoricals') break
            if (s === undefined) s = 0
            else s--
            if (s < 0) s = 5
            handleSetLabel({...selectedEvent}, {score: s})
            break
          case '+':
            if (selectedEvent.type === 'categoricals') break
            if (s === undefined) s = 5
            else s++
            if (s > 5) s = 0
            handleSetLabel({...selectedEvent}, {score: s})
            break
        }
      },
    },
    {
      target: window,
      eventOptions: {
        passive: false,
      },
    }
  )

  function handleGetLabelTrack(key?: string) {
    if (!key) return
    const vionlabs_id = _.get(data, 'vionlabs_id')
    if (!vionlabs_id) {
      console.error('no vionlabs_id in data')
      return
    }
    return getCrdtLabelTrack(vionlabs_id, key)
  }

  function handleSetLabel(event: IEvent, data: object) {
    if (!event.type) return
    const labels = handleGetLabelTrack(event.type)
    if (!labels) return
    const res: ILabel<object> = {
      type: event.type,
      start: event.start,
    }
    // Special case, start:-1 and end:-1 is asset wide label
    if (event.start === -1 || event.end === -1) res.end = -1
    else if (event.end !== undefined && event.end > event.start) res.end = event.end
    res.data = data
    writeToLabelTrack(labels, res)
  }

  function handleSetSelectedEvent(event: IEvent) {
    if (!setSelectedEvent) return
    setSelectedEvent(event)
  }

  function handleSaveTime(startTimeOverride?: number, endTimeOverride?: number) {
    if (!selectedEvent) return
    if (!selectedEvent.type) return
    const labels = handleGetLabelTrack(selectedEvent.type)
    if (!labels) return
    const res: ILabel<object> = {
      type: selectedEvent.type,
      start: selectedEvent.start,
      end: selectedEvent.end,
      ..._.omit(labels.get(_.toString(selectedEvent.start)), 'type', 'start', 'end'),
    }
    const s = startTimeOverride !== undefined ? startTimeOverride : startTime
    const e = endTimeOverride !== undefined ? endTimeOverride : endTime
    if (!_.isEqual(s, res.start)) {
      if (s !== undefined) res.start = s
    }
    if (!_.isEqual(e, res.end)) {
      if (e === undefined) {
        if (res.end !== undefined) delete res.end
      } else res.end = e
    }
    if (res.end === res.start) delete res.end
    handleSetSelectedEvent(res)
    if (res.data !== undefined) {
      // delete previous
      labels.delete(_.toString(selectedEvent.start))
      writeToLabelTrack(labels, res)
    }
    setIsEditing(false)
  }

  function handleDeleteLabel(label: IEvent) {
    // console.log('DELETE', label);
    if (!selectedEvent) return
    const labels = handleGetLabelTrack(selectedEvent.type)
    if (!labels) return
    labels.delete(_.toString(label.start))
  }

  const labelingWidgetWrapClassNames = _.chain([
    'labeling-widget-wrap',
    isEditing ? 'disabled' : undefined,
  ])
    .compact()
    .join(' ')
    .value()

  return (
    <div className='labeling-panel'>
      {selectedEvent !== undefined ?
        <div className='label-form'>
          {/* NOTE: clicks on plot tracks may end up without event type, they should not be labeled and are ignored */}
          {selectedEvent.type ?
            <>
              {setSelectedEvent ?
                <div className='time'>
                  <div className='values'>
                    <div style={{whiteSpace: 'nowrap'}}>{selectedEvent.type}:</div>
                    {isEditing ?
                      <div className='value'>
                        <Input
                          className='ts-edit'
                          value={inputStartTime || 0}
                          onChange={(_e, {value}) => setInputStartTime(value)}
                          size='mini'
                          style={{
                            width: '80px',
                          }}
                        />
                        <SetFromCurrent
                          currentTime={currentTime}
                          tooltip='selection start = current video time (i)'
                          setter={(v) => setInputStartTime(_.toString(v))}
                        />
                      </div>
                    : <div className='value'>
                        {formatTime ?
                          secondsToTimeString(selectedEvent.start)
                        : selectedEvent.start.toFixed(2)}
                      </div>
                    }
                    {isEditing || selectedEvent.end ?
                      <div className='separator' />
                    : null}
                    {isEditing ?
                      <div className='value'>
                        <Input
                          className='ts-edit'
                          value={inputEndTime || 0}
                          onChange={(_e, {value}) => setInputEndTime(value)}
                          size='mini'
                          style={{
                            width: '80px',
                          }}
                        />
                        <SetFromCurrent
                          currentTime={currentTime}
                          tooltip='selection end = current video time (o)'
                          setter={(v) => setInputEndTime(_.toString(v))}
                        />
                      </div>
                    : selectedEvent.end ?
                      <div className='value'>
                        {`${formatTime ? secondsToTimeString(selectedEvent.end) : selectedEvent.end.toFixed(2)}`}
                      </div>
                    : null}
                  </div>
                  <div className='actions'>
                    {!isEditing && selectRandomPrediction ?
                      <Popup
                        size='tiny'
                        content='pick random prediction (r)'
                        openOnTriggerClick={false}
                        closeOnTriggerClick={false}
                        trigger={
                          <Button
                            compact
                            size='tiny'
                            icon='shuffle'
                            onClick={selectRandomPrediction}
                          />
                        }
                      />
                    : null}
                    {isEditing ?
                      <Popup
                        size='tiny'
                        content='update timestamps'
                        openOnTriggerClick={false}
                        closeOnTriggerClick={false}
                        trigger={
                          <Button
                            compact
                            size='tiny'
                            icon='save'
                            color='green'
                            onClick={() => handleSaveTime()}
                          />
                        }
                      />
                    : <Button.Group
                        compact
                        size='tiny'
                      >
                        <Popup
                          size='tiny'
                          content='edit timestamps'
                          openOnTriggerClick={false}
                          closeOnTriggerClick={false}
                          trigger={
                            <Button
                              icon='pencil'
                              color='blue'
                              disabled={
                                readOnly ||
                                !isLabelingAllowed ||
                                (selectedEvent.start === -1 && selectedEvent.end === -1)
                              }
                              onClick={() => setIsEditing(true)}
                            />
                          }
                        />
                        <Popup
                          size='tiny'
                          content='delete label'
                          openOnTriggerClick={false}
                          closeOnTriggerClick={false}
                          trigger={
                            <ConfirmButton
                              icon='delete'
                              color='red'
                              confirmText={
                                labelingWidgetConfig.deleteConfirmText || 'Delete label?'
                              }
                              disabled={
                                readOnly ||
                                !isLabelingAllowed ||
                                labelingWidgetConfig.canDelete !== true ||
                                currentLabelData === undefined
                              }
                              onClick={() => handleDeleteLabel(selectedEvent)}
                            />
                          }
                        />
                      </Button.Group>
                    }
                  </div>
                </div>
              : null}

              {labelingWidgetConfig ?
                <div className={labelingWidgetWrapClassNames}>
                  <labelingWidgetConfig.control
                    disabled={readOnly || isEditing}
                    predictionStats={predictionStats}
                    selectedEvent={selectedEvent}
                    currentLabelData={currentLabelData}
                    setCurrentLabelData={(...args) => handleSetLabel(selectedEvent, ...args)}
                    deleteLabelData={() => handleDeleteLabel(selectedEvent)}
                    implicitLabelData={implicitLabelData}
                    implicitPredictionData={implicitPredictionData}
                  />
                </div>
              : null}
            </>
          : null}

          {selectedEvent.frame_image ?
            <img
              className='frame-image'
              src={selectedEvent.frame_image}
            />
          : null}

          {hideJsonViewer ?
            undefined
          : <div
              style={{
                display: 'flex',
                justifyContent: 'center',
                marginTop: '0.5em',
              }}
            >
              <JsonViewer data={selectedEvent} />
            </div>
          }
        </div>
      : null}
    </div>
  )
}
